import { useState, useEffect, useContext, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  CircularProgress,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Radio,
  RadioGroup,
  FormControlLabel,
  Select,
  MenuItem,
  Switch,
} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import MicIcon from "@mui/icons-material/Mic";
import zoomSdk from "@zoom/appssdk";
import AppBackService from "../services/appback";
import { liveArriving, spokenOf } from "../services/spokenDocs";
import LiveSystemSelect from "./LiveSystemSelect";
import { DEFAULT_LIVE_SYSTEM, writeLiveSystem } from "../liveTap/liveSystem";
import { classifySourceLink, isMeetingKind, sourceLinkLabel } from "../vendor/liveTapLinks/sourceLink";
import hostCapture, { acquireStream, listMicrophones, SOURCE_KIND } from "../liveTap/hostCapture/hostCapture";
import { useHostCaptureActive } from "../hooks/useHostCapture";
import { RootContext } from "../context/root-provider";

const styles = {
  textField: {
    width: "100%",
    marginBottom: "10px",
    marginTop: "6px",
  },
  heading: {
    marginTop: "10px",
  },
  hint: {
    fontSize: "0.78rem",
    color: "var(--color-text-tertiary)",
    opacity: 0.55,
    marginTop: 0,
    marginBottom: "4px",
  },
};

// MUI's toggle/radio/select defaults are near-black; theme them through the
// CSS variables like the rest of the dialog (TT-1102).
const MODE_TOGGLE_SX = {
  marginTop: "10px",
  marginBottom: "4px",
  "& .MuiToggleButton-root": {
    gap: "6px",
    textTransform: "none",
    color: "var(--color-text-secondary)",
    borderColor: "var(--color-border-primary)",
    "&.Mui-selected": {
      color: "var(--color-text-primary)",
      backgroundColor: "var(--color-bg-tertiary)",
      borderColor: "var(--color-accent-primary)",
    },
    "&:hover": { backgroundColor: "var(--color-interactive-hover)" },
  },
};
const RADIO_SX = {
  color: "var(--color-text-secondary)",
  "&.Mui-checked": { color: "var(--color-accent-primary)" },
};
const RADIO_LABEL_SX = {
  "& .MuiFormControlLabel-label": { color: "var(--color-text-primary)", fontSize: "0.9rem" },
};
const MIC_SELECT_SX = {
  width: "100%",
  marginBottom: "10px",
  color: "var(--color-text-primary)",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--color-border-primary)" },
  "& .MuiSvgIcon-root": { color: "var(--color-text-secondary)" },
};

// The per-meeting audio override (TT-1190) as start-body fields: sent only
// when the user flipped the switch (null = untouched), so otherwise the
// server's policy for the meeting class decides.
const retainAudioField = (choice) => (choice === null ? {} : { retain_audio: choice });

// The start response's retain_audio is what the doc actually got. When it
// differs from what the switch showed at Start (an RTMS adopt of a doc the
// webhook already made, say), say so. null on either side is unknown, not a
// mismatch.
function retainMismatchNote(asked, got) {
  if (typeof asked !== "boolean" || typeof got !== "boolean" || asked === got) return "";
  return got
    ? "This meeting's audio is being kept: the meeting was already set up to keep it."
    : "This meeting's audio isn't being kept: the meeting was already set up without it.";
}

const joinNotes = (...notes) => notes.filter(Boolean).join(" ");

// The meeting's plan as Zoom has it (TT-1232): "45 min planned · recurring".
// Empty for an instant meeting, which plans nothing.
export function meetingPlanNote(meeting) {
  if (!meeting) return "";
  const parts = [];
  if (meeting.duration > 0) parts.push(`${meeting.duration} min planned`);
  if (meeting.recurring) parts.push("recurring");
  return parts.join(" · ");
}

const KEEPER_POLL_MS = 3000;
const KEEPER_WARM_TIMEOUT_MS = 60000;

// Poll `check` every KEEPER_POLL_MS until it is true or the warm timeout passes.
async function pollUntil(check) {
  const deadline = Date.now() + KEEPER_WARM_TIMEOUT_MS;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, KEEPER_POLL_MS));
    if (await check()) return true;
  }
  return false;
}

const StartFyreModal = ({ open, setOpen, presetTitle, presetJoinUrl, refreshCallback, showSnackbar }) => {
  const navigate = useNavigate();
  const [title, setTitle] = useState(presetTitle || defaultMeetingTitle());
  const [joinUrl, setJoinUrl] = useState("");
  const [formInvalid, setFormInvalid] = useState(false);
  // Which rendering system the Live Tap page should use (TT-1046) — a per-doc,
  // client-side choice written to localStorage once the doc id exists.
  const [liveSystem, setLiveSystem] = useState(DEFAULT_LIVE_SYSTEM);
  const [fyreStarting, setFyreStarting] = useState(false);
  const [waitingMessage, setWaitingMessage] = useState("");
  const dialogContentEl = useRef();

  // Where the audio comes from (TT-1102): a meeting a bot joins, or this
  // device — a microphone, or a tab/screen shared with its audio.
  const [mode, setMode] = useState("link");
  const [deviceKind, setDeviceKind] = useState(SOURCE_KIND.MIC);
  const [micId, setMicId] = useState("");
  const [mics, setMics] = useState([]);
  const captureBusy = useHostCaptureActive();

  const { userInfo, notifInterval, inZoomClient, runningContext } = useContext(RootContext);
  const inMeeting = inZoomClient && runningContext === "inMeeting";
  const [rtmsStarting, setRtmsStarting] = useState(false);

  // In a meeting the capture may already be running: Zoom auto-start begins
  // the stream with the meeting and the webhook creates the doc (TT-1086).
  // Then this modal is setup, not start — it carries that doc's name and
  // opens it. Otherwise the meeting's topic is the title to beat (TT-1232).
  const [running, setRunning] = useState(null); // { docId, title, meeting } | null
  useEffect(() => {
    if (!open || !inMeeting) {
      setRunning(null);
      return undefined;
    }
    const userId = userInfo?.username;
    let active = true;
    (async () => {
      let meetingUuid = null;
      try {
        meetingUuid = (await zoomSdk.getMeetingUUID())?.meetingUUID || null;
      } catch (error) {
        console.log("getMeetingUUID failed on open:", error);
      }
      let found = null;
      if (userId && meetingUuid) {
        try {
          const res = await AppBackService.getRunningFyre(userId, meetingUuid);
          // `meeting` is what Zoom said about the meeting (topic, agenda,
          // planned length), once the webhook has read it; its keys stay
          // snake_case, axiosExc camelizes only the top level.
          found =
            res && !res.error && res.docId
              ? { docId: res.docId, title: res.title, meeting: res.meeting || null }
              : null;
        } catch (error) {
          console.log("running-capture read failed; treating as none:", error);
        }
      }
      if (!active) return;
      setRunning(found);
      if (presetTitle) return;
      if (found?.title) {
        setTitle(found.title);
        return;
      }
      try {
        // host only; others get no topic and keep the dated default
        const ctx = await zoomSdk.getMeetingContext();
        if (active && ctx?.meetingTopic) setTitle(ctx.meetingTopic);
      } catch (error) {
        console.log("getMeetingContext unavailable:", error);
      }
    })();
    return () => {
      active = false;
    };
  }, [open, inMeeting, userInfo?.username, presetTitle]);

  // Keep this meeting's audio (TT-1190). The switch shows the user's meeting
  // setting, read on every open and held still until it lands, until they flip
  // it; only a flip is sent. Loading starts true so the first paint never
  // shows a switch the read is about to change.
  const [retainDefault, setRetainDefault] = useState(null); // the setting; null = couldn't read it
  const [retainChoice, setRetainChoice] = useState(null); // the flip; null = untouched
  const [retainLoading, setRetainLoading] = useState(true);
  const retainAsked = retainChoice ?? retainDefault;
  const retainUnknown = !retainLoading && retainDefault === null;

  const closeModal = useCallback(() => {
    setFormInvalid(false);
    setOpen(false);
  }, [setOpen]);

  // The start's notice rides the navigation: a snackbar shown here would go
  // with the page this modal sits on. The Live Tap page shows it (TT-1190).
  const openLiveTap = useCallback(
    (docId, notice = null) => {
      if (docId) {
        navigate(`/live-tap/${docId}`, notice ? { state: { notice } } : undefined);
      }
    },
    [navigate]
  );

  useEffect(() => {
    if (!open) {
      setFyreStarting(false);
      setWaitingMessage("");
      setLiveSystem(DEFAULT_LIVE_SYSTEM); // classic stays the default every open
      // the next open starts unread and untouched, even if this one closed mid-read
      setRetainChoice(null);
      setRetainDefault(null);
      setRetainLoading(true);
      // The modal lives in the app shell for the whole signed-in session
      // (TT-1231), not per page: the next open starts clean — today's title,
      // an empty link field, link mode — rather than the last meeting's.
      setTitle(presetTitle || defaultMeetingTitle());
      setJoinUrl("");
      setMode("link");
    }
  }, [open, presetTitle]);

  // A meeting link handed in from outside (dropped, pasted, or on the
  // clipboard — TT-1231) fills the link field on open; the user still
  // presses Add.
  useEffect(() => {
    if (open && presetJoinUrl) {
      setJoinUrl(presetJoinUrl);
      setMode("link");
    }
  }, [open, presetJoinUrl]);

  useEffect(() => {
    if (!open) return undefined;
    const userId = userInfo?.username;
    if (!userId) {
      setRetainLoading(false);
      return undefined;
    }
    let active = true;
    AppBackService.getMediaSettings(userId).then((res) => {
      if (!active) return;
      const meeting = res && !res.error ? res.classes?.meeting : undefined;
      setRetainDefault(typeof meeting === "boolean" ? meeting : null);
      setRetainLoading(false);
    });
    return () => {
      active = false;
    };
  }, [open, userInfo?.username]);

  // Microphone labels only show once the site has had mic permission; the
  // list is still usable before that (the browser picks the default).
  useEffect(() => {
    if (!open || mode !== "device" || !navigator.mediaDevices?.enumerateDevices) return undefined;
    let active = true;
    listMicrophones()
      .then((list) => {
        // before permission Chrome lists one placeholder with an empty id —
        // that is "Default microphone" already, not a second entry
        if (active) setMics(list.filter((d) => d.deviceId));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [open, mode]);

  useEffect(() => {
    if (formInvalid && dialogContentEl.current) {
      dialogContentEl.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [formInvalid]);

  useEffect(() => {
    async function fetchMeetingJoinUrl() {
      if (open && inZoomClient && !joinUrl) {
        try {
          const response = await zoomSdk.getMeetingJoinUrl();
          if (response?.joinUrl) {
            setJoinUrl(response.joinUrl);
          }
        } catch (error) {
          console.log("Unable to get meeting join URL:", error);
        }
      }
    }
    fetchMeetingJoinUrl();
  }, [open, inZoomClient, joinUrl]);

  // The readiness probe already exists: POST /users/{id}/fyre with probe_only.
  const keeperIsWarm = useCallback(async () => {
    try {
      const probe = await AppBackService.startFyre(userInfo?.username, {
        probe_only: true,
        keeper_type: "rtms",
      });
      return (probe?.capacity?.running ?? 0) >= 1;
    } catch (error) {
      // A failed probe must not block capture — assume warm and carry on.
      console.log("Keeper readiness probe failed:", error);
      return true;
    }
  }, [userInfo]);

  // A cold keeper measured 19s from stopped to listening; poll a little past
  // that rather than guessing a fixed sleep.
  const waitForWarmKeeper = useCallback(() => pollUntil(keeperIsWarm), [keeperIsWarm]);

  // TT-1086: after a failed startRTMS, the stub may still become the live doc
  // — the webhook attaches to it by meeting_uuid. Poll its status a little
  // past a cold keeper's attach time before declaring failure. Attached is
  // the doc read's fact (TT-1273): its source arriving live.
  const waitForStubAttached = useCallback(
    (docId) =>
      pollUntil(async () => {
        try {
          return liveArriving(spokenOf(await AppBackService.getSpokenDoc(userInfo?.username, docId)));
        } catch (error) {
          console.log("Stub status check failed:", error);
          return false;
        }
      }),
    [userInfo]
  );

  // This device (TT-1102). Order matters: the media prompt needs the click's
  // activation, so the stream is acquired BEFORE the doc is created; a doc
  // with no capture would be an empty tapestry.
  const startHostCapture = useCallback(async () => {
    if (!title) {
      setFormInvalid(true);
      return;
    }
    if (captureBusy) {
      showSnackbar("A capture is already running in this tab — stop it first.", "warning");
      return;
    }
    setFormInvalid(false);
    let stream;
    try {
      stream = await acquireStream({ kind: deviceKind, deviceId: deviceKind === SOURCE_KIND.MIC ? micId : null });
    } catch (error) {
      console.log("Audio capture not granted:", error);
      showSnackbar(error?.message || "Audio capture was not allowed", "error");
      return;
    }
    const track = stream.getAudioTracks()[0];
    const sourceLabel =
      deviceKind === SOURCE_KIND.SHARE ? `Shared audio: ${track?.label || "screen"}` : track?.label || "Microphone";

    setFyreStarting(true);
    setWaitingMessage("Starting capture on this device...");
    let docId = null;
    try {
      const resp = await AppBackService.startFyre(userInfo?.username, {
        source: "host",
        title,
        ...retainAudioField(retainChoice),
      });
      if (!resp || resp.error || !resp.docId) {
        throw new Error(resp?.msg || "Unable to create the live tapestry");
      }
      docId = resp.docId;
      writeLiveSystem(docId, liveSystem);
      await hostCapture.start({
        userId: userInfo?.username,
        docId,
        stream,
        sourceLabel,
        sourceKind: deviceKind,
        // The doc's own flag, not the switch: this browser is the only copy of
        // the audio. Only an explicit false skips the tee; an unresolved
        // policy (null) keeps, as retention does, and the server's session
        // answer has the final word.
        retainAudio: resp.retainAudio !== false,
      });
      const retainNote = retainMismatchNote(retainAsked, resp.retainAudio);
      setOpen(false);
      refreshCallback();
      notifInterval(refreshCallback, showSnackbar, 20000, 90, true);
      openLiveTap(docId, {
        message: joinNotes("Capturing this device. Keep this tab open while the tapestry draws.", retainNote),
        severity: retainNote ? "warning" : "info",
      });
    } catch (error) {
      console.error("Error starting host capture:", error);
      stream.getTracks().forEach((t) => t.stop());
      if (docId) {
        // The doc exists but nothing will ever drip into it — close it rather
        // than leave a "running" tapestry that never starts.
        AppBackService.postDrip(userInfo?.username, docId, { drip_num: 1, content: "", close: true }).catch(() => {});
        refreshCallback();
      }
      showSnackbar(error?.message || "Unable to start capture", "error");
    } finally {
      setFyreStarting(false);
      setWaitingMessage("");
    }
  }, [
    captureBusy,
    deviceKind,
    liveSystem,
    micId,
    notifInterval,
    openLiveTap,
    refreshCallback,
    retainAsked,
    retainChoice,
    setOpen,
    showSnackbar,
    title,
    userInfo,
  ]);

  const startFyre = useCallback(async () => {
    if (!inMeeting && mode === "device") {
      await startHostCapture();
      return;
    }

    // In-meeting RTMS flow
    if (inMeeting) {
      if (!title) {
        setFormInvalid(true);
        return;
      }
      setFormInvalid(false);
      setRtmsStarting(true);

      try {
        // Get the meeting UUID from the Zoom SDK
        const uuidResp = await zoomSdk.getMeetingUUID();
        const meeting_uuid = uuidResp?.meetingUUID;
        if (!meeting_uuid) {
          showSnackbar("Unable to get meeting UUID from Zoom", "error");
          setRtmsStarting(false);
          return;
        }

        // Create the pending doc stub on the backend
        const resp = await AppBackService.startFyre(userInfo?.username, {
          title,
          meeting_uuid,
          no_bot: true,
          ...retainAudioField(retainChoice),
        });

        if (!resp || resp.error || !resp.docId) {
          showSnackbar(resp?.message || "Unable to create RTMS session", "error");
          setRtmsStarting(false);
          return;
        }
        // an adopted doc was stamped when the webhook made it, before this start
        const retainNote = retainMismatchNote(retainAsked, resp.retainAudio);

        console.log("RTMS FYRE INFO:", { userId: userInfo?.username, docId: resp.docId, meeting_uuid });

        // Record the chosen rendering system for this doc (TT-1046) now that
        // the doc id exists, so the Live Tap page opens straight into it.
        writeLiveSystem(resp.docId, liveSystem);

        // Trigger RTMS via Zoom SDK — unless the stream is already running.
        // Zoom auto-start (an account-admin setting, and one users can now
        // turn on themselves) begins the stream with the meeting; the backend
        // then hands back the webhook's running doc as `adopted`, and calling
        // startRTMS again would throw "start rtms fail" for what is the
        // outcome we wanted (TT-1086).
        if (resp.adopted) {
          console.log("RTMS already running for this meeting; adopted doc", resp.docId);
        } else {
          try {
            const rtmsResp = await zoomSdk.callZoomApi("startRTMS");
            console.log("RTMS startRTMS response:", JSON.stringify(rtmsResp));
          } catch (error) {
            // The other order of the same race: auto-start fired but its
            // webhook had not landed when the stub was created, so the stub is
            // what the webhook attaches to — a few seconds from now on a warm
            // keeper, ~25s on a cold one. Ask the doc before calling it a failure.
            console.log("startRTMS failed; checking whether the stream attached anyway:", error);
            setWaitingMessage("Connecting to the meeting's live stream...");
            const attached = await waitForStubAttached(resp.docId);
            setWaitingMessage("");
            if (!attached) throw error;
          }
        }

        // Do NOT open the Live Tapestry yet. resp.docId is not evidence of a
        // capture: the backend creates the doc stub unconditionally and the
        // keeper is only launched later, by the webhook handler. On a cold
        // keeper the page would sit empty for a minute and read as broken
        // (TT-1065), which is what a reviewer testing once would see.
        //
        // capacity.running, not `ready` — `ready` is true for a merely STOPPED
        // host, because it could be started. Only a running one can attach now.
        const warm = await keeperIsWarm();
        let slowNote = "";
        if (!warm) {
          setWaitingMessage("Starting the capture server...");
          const cameUp = await waitForWarmKeeper();
          if (!cameUp) {
            slowNote =
              "The capture server is taking longer than usual to start. Your Tapestry will begin as soon as it connects.";
          }
          setWaitingMessage("");
        }
        // one snackbar shows at a time, so the two notes share it
        const notice = joinNotes(slowNote, retainNote);

        setOpen(false);
        refreshCallback();
        notifInterval(refreshCallback, showSnackbar, 20000, 90, true);
        openLiveTap(resp.docId, notice ? { message: notice, severity: "warning" } : null);
      } catch (error) {
        console.error("Error starting RTMS:", error);
        showSnackbar("Failed to start RTMS: " + (error.message || "Unknown error"), "error");
      } finally {
        setRtmsStarting(false);
      }
      return;
    }

    // Standard (browser) flow — route by the platform detected from the link
    const link = joinUrl.trim();
    const platform = detectPlatform(link);

    // Google Meet & Microsoft Teams — Recall notetaker via the shared fyre
    // endpoint. Both just pass a bare meeting_url; the backend detects the
    // platform and tags the doc (Recall normalizes every platform behind one API).
    if (platform === "gmeet" || platform === "teams") {
      if (!title) {
        setFormInvalid(true);
        return;
      }
      const label = platformLabel(platform);
      setFormInvalid(false);
      setFyreStarting(true);
      setWaitingMessage(`Starting ${label} capture...`);
      try {
        const resp = await AppBackService.startFyre(userInfo?.username, {
          meeting_url: link,
          title,
          ...retainAudioField(retainChoice),
        });
        if (resp && !resp.error && resp.docId) {
          writeLiveSystem(resp.docId, liveSystem); // TT-1046 — per-doc system choice
          const retainNote = retainMismatchNote(retainAsked, resp.retainAudio);
          setOpen(false);
          refreshCallback();
          notifInterval(refreshCallback, showSnackbar, 20000, 90, true);
          openLiveTap(resp.docId, {
            message: joinNotes(`${label} capture started. Admit the notetaker bot in your meeting.`, retainNote),
            severity: retainNote ? "warning" : "info",
          });
        } else if (resp?.status === "instance_starting") {
          setFyreStarting(false);
          setWaitingMessage("");
          showSnackbar("Recall keeper is starting — try again in a minute.", "info");
        } else {
          setFyreStarting(false);
          setWaitingMessage("");
          showSnackbar(resp?.msg || `Unable to start ${label} capture`, "error");
        }
      } catch (error) {
        console.error(`Error starting ${label} capture:`, error);
        setFyreStarting(false);
        setWaitingMessage("");
        showSnackbar(`Unable to start ${label} capture`, "error");
      }
      return;
    }

    // Zoom in the browser is no longer a capture route. Live Zoom capture is
    // RTMS, and RTMS can only be started from inside the Zoom client: the
    // trigger is a JS SDK call (zoomSdk.callZoomApi("startRTMS")) and the
    // backend keys the session on a meeting UUID that only the SDK can supply.
    // The bot flow this used to fall back to launched a Windows keeper whose
    // hosts have not run since 2026-07, behind a condition that fired only when
    // the user's Zoom token was INVALID — so a healthy account got silence.
    if (platform === "zoom") {
      setFormInvalid(false);
      showSnackbar(
        "Live capture for Zoom runs inside the Zoom client. Open the Tapestry app from your Zoom meeting to start it.",
        "info"
      );
      return;
    }

    setFormInvalid(true);
    if (title && link) {
      showSnackbar("Unrecognized link. Paste a Google Meet or Microsoft Teams link.", "error");
    }
  }, [
    inMeeting,
    mode,
    startHostCapture,
    joinUrl,
    liveSystem,
    notifInterval,
    refreshCallback,
    retainAsked,
    retainChoice,
    setOpen,
    showSnackbar,
    title,
    userInfo,
    openLiveTap,
    keeperIsWarm,
    waitForWarmKeeper,
    waitForStubAttached,
  ]);

  const detectedPlatform = detectPlatform(joinUrl);
  const detected = !joinUrl.trim()
    ? null
    : detectedPlatform === "gmeet"
    ? { label: "Detected: Google Meet", color: "var(--color-success)" }
    : detectedPlatform === "zoom"
    ? { label: "Zoom live capture runs inside the Zoom client — open the Tapestry app from your meeting", color: "var(--color-text-secondary)" }
    : detectedPlatform === "teams"
    ? { label: "Detected: Microsoft Teams", color: "var(--color-success)" }
    : { label: "Unrecognized link — paste a Google Meet or Microsoft Teams link", color: "var(--color-error)" };

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={closeModal}>
      <DialogTitle>Live Tapestry</DialogTitle>

      <DialogContent ref={dialogContentEl}>
        {!presetTitle ? (
          <>
            <DialogContentText sx={{ fontSize: "0.8rem", color: "var(--color-text-tertiary)", marginBottom: "4px" }}>
              Meeting Title
            </DialogContentText>
            <TextField
              sx={styles.textField}
              error={formInvalid && !title}
              helperText={formInvalid && !title && "Please enter a meeting title"}
              placeholder="Enter a title for this meeting"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={fyreStarting}
            />
          </>
        ) : (
          <DialogContentText sx={{ ...styles.heading, marginTop: 0 }}>Meeting: {presetTitle}</DialogContentText>
        )}

        {!inMeeting && (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={mode}
            onChange={(_, value) => value && setMode(value)}
            disabled={fyreStarting}
            sx={MODE_TOGGLE_SX}>
            <ToggleButton value="link">
              <LinkIcon fontSize="small" /> Meeting link
            </ToggleButton>
            <ToggleButton value="device">
              <MicIcon fontSize="small" /> This device
            </ToggleButton>
          </ToggleButtonGroup>
        )}

        {!inMeeting && mode === "device" && (
          <>
            <DialogContentText sx={styles.hint}>
              Draw a live Tapestry from the audio of this computer — no meeting bot. Keep this tab open while it runs.
            </DialogContentText>
            <RadioGroup value={deviceKind} onChange={(e) => setDeviceKind(e.target.value)}>
              <FormControlLabel
                value={SOURCE_KIND.MIC}
                control={<Radio size="small" sx={RADIO_SX} />}
                disabled={fyreStarting}
                sx={RADIO_LABEL_SX}
                label="Microphone — the room, or you"
              />
              {deviceKind === SOURCE_KIND.MIC && (
                <Select
                  size="small"
                  displayEmpty
                  value={micId}
                  onChange={(e) => setMicId(e.target.value)}
                  disabled={fyreStarting}
                  sx={MIC_SELECT_SX}>
                  <MenuItem value="">Default microphone</MenuItem>
                  {mics.map((d, i) => (
                    <MenuItem key={d.deviceId} value={d.deviceId}>
                      {d.label || `Microphone ${i + 1}`}
                    </MenuItem>
                  ))}
                </Select>
              )}
              <FormControlLabel
                value={SOURCE_KIND.SHARE}
                control={<Radio size="small" sx={RADIO_SX} />}
                disabled={fyreStarting}
                sx={RADIO_LABEL_SX}
                label="Share audio — a browser tab, or this screen"
              />
            </RadioGroup>
            {deviceKind === SOURCE_KIND.SHARE && (
              <DialogContentText sx={styles.hint}>
                In the picker, choose a tab (any OS) or your entire screen (Windows) and tick "Share audio". A meeting
                playing in another tab works; so does anything playing on this machine.
              </DialogContentText>
            )}
            {captureBusy && (
              <DialogContentText sx={{ ...styles.hint, color: "var(--color-error)", opacity: 1 }}>
                A capture is already running in this tab. Stop it from its Live Tap page first.
              </DialogContentText>
            )}
          </>
        )}

        {inMeeting ? (
          <>
            <DialogContentText sx={styles.heading}>
              {running
                ? 'This meeting\'s live capture is already running. Name its Tapestry, then click "Open".'
                : 'Enter a title for this meeting\'s live Tapestry, then click "Add" to start Tapestry generation.'}
            </DialogContentText>
            {/* What the host wrote in Zoom before the meeting (TT-1232): the
                Tapestry draws on the agenda too. */}
            {running?.meeting?.agenda && (
              <>
                <DialogContentText sx={styles.heading}>Agenda</DialogContentText>
                <DialogContentText sx={{ ...styles.hint, opacity: 0.8, whiteSpace: "pre-line" }}>
                  {running.meeting.agenda}
                </DialogContentText>
              </>
            )}
            {meetingPlanNote(running?.meeting) && (
              <DialogContentText sx={styles.hint}>{meetingPlanNote(running.meeting)}</DialogContentText>
            )}
          </>
        ) : mode === "device" ? null : (
          <>
            <DialogContentText sx={styles.heading}>Meeting link</DialogContentText>
            <DialogContentText sx={styles.hint}>Google Meet · Microsoft Teams</DialogContentText>
            <TextField
              sx={styles.textField}
              error={formInvalid && !joinUrl}
              placeholder="https://meet.google.com/…   ·   https://teams.microsoft.com/…"
              value={joinUrl}
              onChange={(e) => setJoinUrl(e.target.value)}
              disabled={fyreStarting}
            />
            {detected && (
              <DialogContentText
                sx={{ fontSize: "0.8rem", color: detected.color, marginTop: "-2px", marginBottom: "6px" }}>
                {detected.label}
              </DialogContentText>
            )}
          </>
        )}

        {/* TT-1046 — choose which rendering system the Live Tap page uses.
            Classic stays the default; scene is experimental scaffolding while
            the direction settles. */}
        <DialogContentText sx={styles.heading}>Tapestry style</DialogContentText>
        <DialogContentText sx={styles.hint}>How the live Tapestry is drawn</DialogContentText>
        <LiveSystemSelect heading="" value={liveSystem} onChange={setLiveSystem} />

        <FormControlLabel
          sx={{ ...RADIO_LABEL_SX, ...styles.heading }}
          label="Keep meeting audio"
          control={
            <Switch
              size="small"
              checked={retainAsked === true}
              onChange={(e) => setRetainChoice(e.target.checked)}
              disabled={fyreStarting || rtmsStarting || retainLoading || retainUnknown}
            />
          }
        />
        <DialogContentText sx={styles.hint}>
          {retainUnknown
            ? "Couldn't load your setting, so this meeting uses your default from Settings."
            : "Saves this meeting's audio with its document. Your default for meetings is in Settings."}
        </DialogContentText>

        {waitingMessage && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 3, mb: 1 }}>
            {(fyreStarting || rtmsStarting) && <CircularProgress size={40} sx={{ mb: 2 }} />}
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {waitingMessage}
            </Typography>
            {(fyreStarting || rtmsStarting) && (
              <Typography variant="body2" sx={{ mt: 1, color: "var(--color-text-secondary)" }}>
                The server is waking up. Please wait...
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeModal}>Cancel</Button>
        {/* "Open" is the same start: the backend adopts the running doc with
            this title (TT-1086) and the flow skips startRTMS, then opens it. */}
        <Button onClick={startFyre} disabled={rtmsStarting || fyreStarting || (mode === "device" && captureBusy)}>
          {rtmsStarting || fyreStarting
            ? "Starting..."
            : !inMeeting && mode === "device"
              ? "Start capture"
              : inMeeting && running
                ? "Open"
                : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
StartFyreModal.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  presetTitle: PropTypes.string,
  presetJoinUrl: PropTypes.string,
  refreshCallback: PropTypes.func,
  showSnackbar: PropTypes.func,
};

export default StartFyreModal;

// The meeting platform of a pasted link: null when empty, "unknown" for a
// link this modal has no start for (sourceLink classifies; TT-1231).
function detectPlatform(url) {
  if (!(url || "").trim()) return null;
  const kind = classifySourceLink(url)?.kind;
  return isMeetingKind(kind) ? kind : "unknown";
}

const platformLabel = sourceLinkLabel;

function defaultMeetingTitle() {
  const now = new Date();
  return `Tapestry ${now.toLocaleDateString()}`;
}
